import { useCallback } from 'react'

import { ActionIcon, Box, Loader, Tooltip } from '@mantine/core'

import { useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_STEPS_METRICS } from 'src/graphql/steps.graphql'
import { ApplicantStage } from 'src/graphql/types/applicants'
import { StepStatus } from 'src/graphql/types/steps'
import IconInfoCircle from 'src/icons/IconInfoCircle'
import IconRightArrow from 'src/icons/IconRightArrow'
import { formatStepType } from 'src/lib/formatters'
import { cn } from 'src/utils'

const ProspectChecklist = ({ selectedRecruiters, onMetricClick }) => {
  const labelFor = (metric) =>
    metric.customStepId
      ? metric.customStepLabel ?? formatStepType(metric.name)
      : formatStepType(metric.name)

  const onError = useCallback(() => {
    toast('Unable to get steps metrics, please try again.', 'error')
  }, [])

  const { data: stepsData, loading: loadingStepsMetrics } = useQuery(
    GET_STEPS_METRICS,
    {
      variables: {
        filters: {
          recruiters: selectedRecruiters.map((recruiter) => recruiter.value),
          OR: [
            {
              status: StepStatus.PENDING_REVIEW,
            },
          ],
          applicantStages: [ApplicantStage.PROSPECT],
        },
      },
      onError,
    }
  )

  const pendingActions = stepsData?.stepMetrics?.items

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded border border-doubleNickel-gray-200 p-3 shadow-sm">
      <div className="mb-2 flex flex-row items-center justify-between text-base font-medium text-doubleNickel-gray-800">
        <div className="flex flex-row items-center gap-1.5">
          Checklist
          <Tooltip
            label="Shows actions that require your review for active prospects."
            withArrow
            position="top"
            className="text-xs"
          >
            <div className="flex items-center">
              <IconInfoCircle className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-500" />
            </div>
          </Tooltip>
        </div>
      </div>
      <div
        className={cn(
          'grid grid-cols-4 bg-doubleNickel-gray-50 p-2 text-xs font-semibold text-doubleNickel-gray-700'
        )}
      >
        <div className="col-span-2">Category</div>
        <div>Count</div>
        <div className="invisible">placeholder</div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-auto">
        {loadingStepsMetrics ? (
          <div className="flex flex-1 flex-col items-center justify-center">
            <Loader size="sm" color="gray" />
          </div>
        ) : (
          <>
            {pendingActions?.map((metric) => (
              <Box
                key={metric.customStepId ?? metric.name}
                onClick={() =>
                  onMetricClick(
                    'steps',
                    metric.customStepId
                      ? `CUSTOM:${metric.customStepId}:PENDING_REVIEW`
                      : `${metric.name}-PENDING_REVIEW`
                  )
                }
              >
                <div
                  className={cn(
                    'border-bg-doubleNickel-gray-600 grid min-h-8 cursor-pointer grid-cols-4 items-center px-2 text-xs text-doubleNickel-gray-800 hover:bg-doubleNickel-gray-50'
                  )}
                >
                  <div className="col-span-2">{labelFor(metric)}</div>
                  <div>{metric.value}</div>
                  <Tooltip
                    label={`Go to prospects - ${labelFor(metric)}`}
                    withArrow
                    position="top"
                    className="text-xs"
                  >
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      className="ml-auto"
                    >
                      <IconRightArrow className="h-4 w-4 fill-none stroke-doubleNickel-gray-700" />
                    </ActionIcon>
                  </Tooltip>
                </div>
              </Box>
            ))}
          </>
        )}
        {pendingActions && pendingActions.length === 0 && (
          <div className="flex flex-1 items-center justify-center text-sm font-medium text-doubleNickel-gray-500">
            You have no pending actions
          </div>
        )}
      </div>
    </div>
  )
}

export default ProspectChecklist
